import { FC, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { ToastContainer } from '@savgroup-front-common/core/src/atoms/Toast';
import { ConfirmationContextProvider } from '@savgroup-front-common/core/src/components/Confirmation/ConfirmationContextProvider';
import { GraphQlSseContextProvider } from '@savgroup-front-common/core/src/components/GraphqlSseContext/GraphqlSseContext';
import { NewUiProvider } from '@savgroup-front-common/core/src/components/NewUiProvider/NewUiProvider';
import { BaseLoader } from '@savgroup-front-common/core/src/molecules/BaseLoader';
import AppErrorBoundary from '@savgroup-front-common/core/src/molecules/ErrorBoundary/AppErrorBoundary';
import { ToastsProvider } from '@savgroup-front-common/core/src/molecules/NotificationsProvider';
import { ExternalPortal } from '@savgroup-front-common/core/src/protons/ExternalPortal/ExternalPortal';
import { KeyboardLayoutProvider } from '@savgroup-front-common/core/src/protons/KeyboardLayoutProvider';

import { logCritical } from '../../configuration';

import { buildStore } from './App.config';
import { AppContextProvider } from './App.context';
import DebugLayout from './DebugLayout';
import GlobalStyleManager from './GlobalStyleManager';
import Layout from './layout/Layout';
import PocIntlManager from './PocIntlManager';
import { ThemeProvider } from './ThemeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const App: FC = () => {
  try {
    return (
      <NewUiProvider forcedIsNewUiEnabled>
        <GlobalStyleManager />
        <ToastsProvider>
          <ReduxProvider store={buildStore().store}>
            <KeyboardLayoutProvider>
              <ThemeProvider>
                <PocIntlManager>
                  <AppErrorBoundary>
                    <GraphQlSseContextProvider>
                      <GraphQlSseContextProvider>
                        <ConfirmationContextProvider>
                          <Suspense fallback={<BaseLoader />}>
                            <QueryClientProvider client={queryClient}>
                              <ReactQueryDevtools initialIsOpen={false} />
                              <ExternalPortal>
                                <ToastContainer topOffset="4rem" />
                              </ExternalPortal>

                              <AppContextProvider>
                                <BrowserRouter>
                                  <Routes>
                                    <Route path="/*" element={<DebugLayout />}>
                                      <Route path="*" element={<Layout />} />
                                    </Route>
                                  </Routes>
                                </BrowserRouter>
                              </AppContextProvider>
                            </QueryClientProvider>
                          </Suspense>
                        </ConfirmationContextProvider>
                      </GraphQlSseContextProvider>
                    </GraphQlSseContextProvider>
                  </AppErrorBoundary>
                </PocIntlManager>
              </ThemeProvider>
            </KeyboardLayoutProvider>
          </ReduxProvider>
        </ToastsProvider>
      </NewUiProvider>
    );
  } catch (err) {
    logCritical(err);
    throw err;
  }
};

export default App;
